/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, Printer, QrCode, UserCheck, ChevronRight } from 'lucide-react';

/**
 * Phase 326c — Self-Signup Funnel Insights Tile
 * ---------------------------------------------
 * 3-stage funnel for the academy_owner dashboard:
 *   1. Posters Downloaded
 *   2. QR Scans
 *   3. Signups Completed
 *
 * Self-hides when ALL three numbers are zero (a brand-new academy that
 * hasn't moved the needle yet shouldn't get a flat empty tile).
 */
export default function FunnelInsightsTile({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/academy/signup-funnel?days=${days}`);
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card
        className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-white"
        data-testid="funnel-insights-tile-loading"
      >
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-1/3" />
          <div className="grid grid-cols-3 gap-3 pt-1">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { posters_downloaded = 0, qr_scans = 0, signups_completed = 0,
    scan_to_signup_pct = 0, poster_to_signup_pct = 0, window_days = 30 } = data;

  // Self-hide on all-zeros so the dashboard doesn't show a vapid tile to brand-new academies.
  if (!posters_downloaded && !qr_scans && !signups_completed) return null;

  const stages = [
    {
      label: 'Posters Downloaded',
      value: posters_downloaded,
      icon: Printer,
      color: 'text-sky-700',
      bg: 'bg-sky-50',
      ring: 'ring-sky-200',
      testId: 'funnel-posters-downloaded',
    },
    {
      label: 'QR Scans',
      value: qr_scans,
      icon: QrCode,
      color: 'text-amber-700',
      bg: 'bg-amber-50',
      ring: 'ring-amber-200',
      testId: 'funnel-qr-scans',
    },
    {
      label: 'Signups Completed',
      value: signups_completed,
      icon: UserCheck,
      color: 'text-emerald-700',
      bg: 'bg-emerald-50',
      ring: 'ring-emerald-200',
      testId: 'funnel-signups-completed',
    },
  ];

  return (
    <Card
      className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-white overflow-hidden relative"
      data-testid="funnel-insights-tile"
    >
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-emerald-200/40 blur-2xl pointer-events-none" />
      <CardContent className="p-5 relative">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-medium uppercase tracking-wide">
              <TrendingUp className="h-3.5 w-3.5" />
              Signup Funnel · Last {window_days} days
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 leading-tight mt-1">
              Your AI-powered onboarding pipeline
            </h3>
          </div>
        </div>

        {/* 3-stage funnel */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 items-stretch">
          {stages.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="relative" data-testid={s.testId}>
                <div className={`rounded-xl ring-1 ${s.ring} ${s.bg} p-3 sm:p-4 h-full flex flex-col items-center text-center`}>
                  <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${s.color} mb-1.5`} />
                  <div className={`text-2xl sm:text-3xl font-bold ${s.color}`}>
                    {s.value.toLocaleString('en-IN')}
                  </div>
                  <div className="text-[10px] sm:text-xs text-slate-600 mt-1 leading-tight">
                    {s.label}
                  </div>
                </div>
                {i < stages.length - 1 && (
                  <ChevronRight
                    className="hidden sm:block absolute top-1/2 -right-2.5 -translate-y-1/2 h-5 w-5 text-emerald-400/70 pointer-events-none"
                    strokeWidth={2.5}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Conversion footer */}
        {(qr_scans > 0 || posters_downloaded > 0) && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-emerald-100">
            <div data-testid="funnel-scan-to-signup">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Scan → Signup</div>
              <div className="text-sm font-semibold text-slate-900">
                {scan_to_signup_pct.toLocaleString('en-IN', { maximumFractionDigits: 1 })}%
              </div>
            </div>
            <div data-testid="funnel-poster-to-signup">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Poster → Signup</div>
              <div className="text-sm font-semibold text-slate-900">
                {poster_to_signup_pct.toLocaleString('en-IN', { maximumFractionDigits: 1 })}%
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
